import * as React from 'react';
import { Grid, styled } from '@mui/material';
import { Colors, Text, Icon, SelectableCard } from 'library';
import { useHistory } from 'react-router';
import SignupForm from './SignUpForm';
import SignUpLoginTab from 'parent-portal/public/SignUpLoginTab';
import SignUpLoginHeader from 'parent-portal/public/SignUpLoginHeader';
import SignUpLoginContainer from 'parent-portal/public/SignUpLoginContainer';
import BusinessIcon from '@mui/icons-material/Business';
import SchoolIcon from '@mui/icons-material/School';
import theme from 'theme';
import TeacherDownloadCard from './TeacherDownloadCard';

type Role = 'program' | 'teacher' | null;

export default function SignUpPage() {
    const history = useHistory();
    const [role, setRole] = React.useState<Role>(null);

    return (
        <SignUpLoginContainer>
            <FormContainer item md={12} lg={6}>
                <Grid container justifyContent="center">
                    <SignUpLoginTab />
                </Grid>

                <Content container>
                    <Grid container item xs={12}>
                        <SignUpLoginHeader>Create an Account</SignUpLoginHeader>
                    </Grid>

                    <RolePickerContainer>
                        <Text variant="body1" bold>
                            I am a...
                        </Text>
                        <RoleCardRow>
                            <SelectableCard
                                selected={role === 'program'}
                                faded={role === 'teacher'}
                                onClick={() => setRole('program')}
                                style={{ minHeight: 180 }}
                            >
                                <Icon
                                    size={56}
                                    icon={<BusinessIcon sx={{ fontSize: 28, color: 'inherit' }} />}
                                    backgroundColor={
                                        role === 'program'
                                            ? Colors.turquoise
                                            : role === 'teacher'
                                              ? Colors.disabledGrey
                                              : Colors.lightGrey
                                    }
                                    color={role === 'program' ? Colors.white : Colors.darkNavy}
                                    style={{ transition: 'all 0.2s ease', marginBottom: 4 }}
                                />
                                <Text variant="body1" bold>
                                    Childcare Program
                                </Text>
                                <Text variant="caption" color={Colors.grey}>
                                    I operate a childcare center and I&apos;m looking for staff
                                </Text>
                            </SelectableCard>
                            <SelectableCard
                                selected={role === 'teacher'}
                                faded={role === 'program'}
                                onClick={() => setRole('teacher')}
                                style={{ minHeight: 180 }}
                            >
                                <Icon
                                    size={56}
                                    icon={<SchoolIcon sx={{ fontSize: 28, color: 'inherit' }} />}
                                    backgroundColor={
                                        role === 'teacher'
                                            ? Colors.turquoise
                                            : role === 'program'
                                              ? Colors.disabledGrey
                                              : Colors.lightGrey
                                    }
                                    color={role === 'teacher' ? Colors.white : Colors.darkNavy}
                                    style={{ transition: 'all 0.2s ease', marginBottom: 4 }}
                                />
                                <Text variant="body1" bold>
                                    Teacher
                                </Text>
                                <Text variant="caption" color={Colors.grey}>
                                    I&apos;m a childcare professional looking for work
                                </Text>
                            </SelectableCard>
                        </RoleCardRow>
                    </RolePickerContainer>

                    {role === 'program' && (
                        <SignupForm
                            onComplete={() => history.push('/home')}
                            hideReferral={false}
                            businessSignup={true}
                            modal={false}
                            skipToPhone={false}
                            hideHeader={true}
                            onNotBusinessClick={() => {
                                setRole('teacher');
                                window.scrollTo(0, 0);
                            }}
                        />
                    )}
                    {role === 'teacher' && <TeacherDownloadCard />}
                </Content>
            </FormContainer>
        </SignUpLoginContainer>
    );
}

const FormContainer = styled(Grid)({
    padding: 40,
    overflowY: 'auto',
    maxHeight: '100%',
    [theme.breakpoints.down('md')]: {
        overflowY: 'visible',
        maxHeight: 'none',
        padding: 20,
    },
});

const Content = styled(Grid)({
    padding: '12%',
    [theme.breakpoints.down('md')]: {
        padding: '3%',
    },
    paddingTop: '5%',
});

const RolePickerContainer = styled('div')({
    marginTop: 16,
    marginBottom: 24,
    width: '100%',
});

const RoleCardRow = styled('div')(({ theme: t }) => ({
    display: 'flex',
    gap: 16,
    marginTop: 8,
    [t.breakpoints.down('md')]: {
        flexDirection: 'column',
    },
}));
